/* Final Homepage modules only. Existing Batch A video controller is unchanged. */
const chapterInstances = new Set();
class RopetChapters extends HTMLElement {
  connectedCallback() {
    if (this.running) return;
    this.running = true;
    chapterInstances.add(this);
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.mode = this.dataset.rpV2Mode;
    this.mobile = matchMedia('(max-width:800px)');
    this.reduced = matchMedia('(prefers-reduced-motion:reduce)');
    this.panels = [...this.querySelectorAll('[data-rp-v2-panel]')];
    this.tabs = [...this.querySelectorAll('[data-rp-v2-tab]')];
    this.videos = [...this.querySelectorAll('video')];
    this.active = 0; this.pinned = null; this.near = false; this.visible = false; this.frame = 0;
    this.pending = new WeakMap();
    this.rail = this.querySelector('[data-rp-v2-rail]');
    this.reveal = this.querySelector('[data-rp-v2-reveal]');
    this.nearObserver = new IntersectionObserver(entries => {this.near=entries[0].isIntersecting;this.sync();},{rootMargin:'200px 0px'});
    this.visibleObserver = new IntersectionObserver(entries => {this.visible=entries[0].isIntersecting;this.sync();});
    this.nearObserver.observe(this);this.visibleObserver.observe(this);
    this.classList.add('rp-v2-enhanced');
    this.videos.forEach(video => {
      if (!video.dataset.rpV2Manual) {video.muted=true;video.defaultMuted=true;video.playsInline=true;}
      video.addEventListener('playing',()=>video.parentElement.classList.add('rp-v2-loaded'),{signal});
      video.addEventListener('error',()=>video.parentElement.classList.remove('rp-v2-loaded'),{signal});
    });
    this.tabs.forEach((tab,index)=> {
      tab.addEventListener('click',()=>this.select(index,true),{signal});
      tab.addEventListener('keydown',event=> {
        let next;
        if(event.key==='ArrowRight')next=(index+1)%this.tabs.length;
        if(event.key==='ArrowLeft')next=(index+this.tabs.length-1)%this.tabs.length;
        if(event.key==='Home')next=0;
        if(event.key==='End')next=this.tabs.length-1;
        if(next!==undefined){event.preventDefault();this.tabs[next].focus();this.select(next,true);}
      },{signal});
    });
    if(this.mode==='rail')this.panels.forEach((panel,index)=>{
      ['click','focus','pointerenter'].forEach(type=>panel.addEventListener(type,()=>this.select(index),{signal}));
      panel.addEventListener('keydown',event=>{
        if(!['ArrowLeft','ArrowRight'].includes(event.key))return;
        event.preventDefault();const next=(index+(event.key==='ArrowRight'?1:-1)+this.panels.length)%this.panels.length;
        this.panels[next].focus();this.select(next);
      },{signal});
    });
    if(this.mode==='rail'&&this.rail){
      let swipe=null;
      this.rail.addEventListener('pointerdown',event=>{swipe={x:event.clientX,y:event.clientY,moved:false};},{signal});
      this.rail.addEventListener('pointermove',event=>{if(swipe&&(Math.abs(event.clientX-swipe.x)>8||Math.abs(event.clientY-swipe.y)>8))swipe.moved=true;},{signal});
      this.rail.addEventListener('pointercancel',()=>{swipe=null;},{signal});
      this.rail.addEventListener('click',event=>{
        if(event.detail>0&&swipe?.moved&&event.target.closest('a.rp-v2-personalization-card'))event.preventDefault();
        swipe=null;
      },{capture:true,signal});
    }
    this.querySelectorAll('[data-rp-v2-play]').forEach(button=>button.addEventListener('click',()=>{
      const video=button.parentElement.querySelector('video');
      this.videos.forEach(other=>{if(other!==video)other.pause();});
      if(!video.getAttribute('src')){video.src=video.dataset.rpV2Manual;video.load();}
      video.play().then(()=>{if(this.running)button.hidden=true;else video.pause();},()=>{if(this.running)button.hidden=false;});
    },{signal}));
    const queue=()=>{if(!this.frame)this.frame=requestAnimationFrame(()=>{this.frame=0;this.update();});};
    if(['intelligence','memory'].includes(this.mode))window.addEventListener('scroll',queue,{passive:true,signal});
    if(this.rail)this.rail.addEventListener('scroll',queue,{passive:true,signal});
    window.addEventListener('resize',queue,{passive:true,signal});
    this.mobile.addEventListener('change',()=>{this.release();this.pinned=null;this.update();},{signal});
    this.reduced.addEventListener('change',()=>{this.release();this.update();},{signal});
    document.addEventListener('visibilitychange',()=>this.sync(),{signal});
    this.addEventListener('shopify:block:select',event=>{
      const index=this.panels.findIndex(panel=>panel.dataset.rpV2Panel===event.detail.blockId);
      if(index>=0){this.pinned=index;this.select(index);}
    },{signal});
    this.addEventListener('shopify:block:deselect',()=>{this.pinned=null;this.update();},{signal});
    this.update();
  }
  select(index,fromTab=false) {
    if(index<0||index>=this.panels.length)return;
    if(fromTab&&!this.mobile.matches)this.pinned=index;
    this.active=index;
    this.panels.forEach((panel,i)=>{
      const active=i===index;
      panel.classList.toggle('rp-v2-active',active);
      if(this.mode==='intelligence')panel.hidden=!active;
      if(this.mode==='rail' && panel.tagName==='BUTTON')panel.setAttribute('aria-pressed',String(active));
    });
    this.tabs.forEach((tab,i)=>{tab.setAttribute('aria-selected',String(i===index));tab.tabIndex=i===index?0:-1;});
    this.sync();
  }
  update() {
    if(!this.running)return;
    if(this.mode==='intelligence'&&!this.mobile.matches&&!this.reduced.matches) {
      const rect=this.getBoundingClientRect();const travel=Math.max(1,rect.height-innerHeight+90);
      const progress=Math.max(0,Math.min(.999,(90-rect.top)/travel));
      this.select(this.pinned??Math.floor(progress*this.panels.length));
    } else if(this.mode==='rail'&&this.mobile.matches&&this.rail&&this.panels.length) {
      const center=this.rail.getBoundingClientRect().left+this.rail.clientWidth/2;
      let best=0,distance=Infinity;
      this.panels.forEach((panel,i)=>{const rect=panel.getBoundingClientRect();const d=Math.abs(rect.left+rect.width/2-center);if(d<distance){distance=d;best=i;}});
      this.select(this.pinned??best);
    } else if(this.panels.length)this.select(this.pinned??this.active);
    if(this.mode==='memory'&&this.reveal) {
      const rect=this.getBoundingClientRect();
      const progress=(90-rect.top)/Math.max(1,rect.height-innerHeight+90);
      const opacity=this.mobile.matches||this.reduced.matches?1:Math.max(0,Math.min(1,(progress-.08)/.25));
      this.style.setProperty('--rp-v2-reveal',String(opacity));
      this.reveal.inert=opacity<.04;this.reveal.setAttribute('aria-hidden',String(opacity<.04));
    }
    this.sync();
  }
  wants(video) {
    const panel=video.closest('[data-rp-v2-panel]');
    return this.running&&this.near&&this.visible&&!document.hidden&&!this.reduced.matches&&(!panel||this.panels[this.active]===panel);
  }
  sync() {
    if(!this.running)return;
    this.videos.forEach(video=>{
      if(video.dataset.rpV2Manual){if(!this.visible||document.hidden)video.pause();return;}
      const panel=video.closest('[data-rp-v2-panel]');const active=!panel||this.panels[this.active]===panel;
      if(!active||!this.near||this.reduced.matches){video.pause();return;}
      const device=this.mobile.matches?'Mobile':'Desktop';
      const source=video.dataset['rpV2'+device]||video.dataset.rpV2Desktop;
      if(video.getAttribute('src')!==source){video.pause();video.parentElement.classList.remove('rp-v2-loaded');video.poster=video.dataset['rpV2'+device+'Poster']||video.dataset.rpV2DesktopPoster;video.src=source;video.preload='metadata';video.load();}
      if(!this.wants(video)){video.pause();return;}
      if(this.pending.has(video)||!video.paused)return;
      const pending=video.play();this.pending.set(video,pending);
      pending.then(()=>{if(!this.wants(video))video.pause();},()=>{/* Keep the poster when a browser declines playback. */}).finally(()=>{
        if(this.pending.get(video)===pending)this.pending.delete(video);
      });
    });
  }
  release() {
    this.videos.forEach(video=>{video.pause();video.removeAttribute('src');if(!video.dataset.rpV2Manual)video.removeAttribute('poster');else {const button=video.parentElement.querySelector('[data-rp-v2-play]');if(button)button.hidden=false;}video.load();video.parentElement.classList.remove('rp-v2-loaded');});
    this.pending=new WeakMap();
  }
  disconnectedCallback(){this.destroy();}
  destroy() {
    if(!this.running)return;
    this.running=false;this.abort.abort();this.nearObserver.disconnect();this.visibleObserver.disconnect();cancelAnimationFrame(this.frame);this.release();this.classList.remove('rp-v2-enhanced');chapterInstances.delete(this);
    if(this.reveal){this.reveal.inert=false;this.reveal.removeAttribute('aria-hidden');}
  }
}
class RopetHeaderState extends HTMLElement {
  connectedCallback(){
    if(this.observer)return;
    this.hero=this.closest('.rp-v2-hero');
    // Explicitly authorized presentation-only bridge to the existing Header.
    this.header=document.querySelector('.section-header');
    if(!this.hero||!this.header)return;
    this.header.classList.add('rp-v2-header-ready');
    this.observer=new IntersectionObserver(entries=>this.header.classList.toggle('rp-v2-on-hero',entries[0].isIntersecting),{rootMargin:'-80px 0px 0px 0px'});
    this.observer.observe(this.hero);
  }
  disconnectedCallback(){this.observer?.disconnect();this.observer=null;this.header?.classList.remove('rp-v2-header-ready','rp-v2-on-hero');}
}
if(!customElements.get('rp-v2-chapters'))customElements.define('rp-v2-chapters',RopetChapters);
if(!customElements.get('rp-v2-header-state'))customElements.define('rp-v2-header-state',RopetHeaderState);
document.addEventListener('shopify:section:unload',event=>{for(const root of chapterInstances)if(event.target.contains(root))root.destroy();event.target.querySelectorAll('rp-v2-header-state').forEach(root=>root.disconnectedCallback());});
document.addEventListener('shopify:section:load',event=>event.target.querySelectorAll('rp-v2-chapters,rp-v2-header-state').forEach(root=>root.connectedCallback()));
document.addEventListener('shopify:section:select',event=>event.target.querySelectorAll('rp-v2-chapters').forEach(root=>root.update()));
